import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Lock, LockOpen, Plus, Check, Trash, ArrowUp, ArrowDown, Eye, Receipt, X, ArrowsClockwise } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount, currency = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(amount || 0);

const BillingConfigPanel = ({ clientId, month, monthLabel, currency, onRefresh, clientData }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [combinedRevenue, setCombinedRevenue] = useState(null);
  const [billingCalc, setBillingCalc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const resyncFromShopify = async () => {
    if (!window.confirm("Fetch fresh revenue from Shopify for this client+month? This bypasses the cache and takes ~5-10 seconds.")) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/shopify/resync-revenue?month=${month}`);
      if (res.data?.success) {
        const t = res.data.totals || {};
        alert(`Fresh sync done!\nTotal Sales: ${formatCurrency(t.total_collected || 0, currency)}\nOrders: ${t.order_count || 0}\nSynced at: ${new Date(res.data.synced_at).toLocaleString()}`);
        fetchData();
        onRefresh?.();
      }
    } catch (e) {
      alert("Resync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setResyncing(false);
    }
  };
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [adjForm, setAdjForm] = useState({ adjustment_type: "plus", amount: 0, reference_no: "", note: "" });

  // Manual revenue editing
  const [manualRevenue, setManualRevenue] = useState(null);
  const [editingRevenue, setEditingRevenue] = useState(false);

  // Invoice preview state
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState("fixed");
  const [deductFixed, setDeductFixed] = useState(false);
  const [customDeduction, setCustomDeduction] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [editedDescription, setEditedDescription] = useState("");
  const [editedCombinedDescs, setEditedCombinedDescs] = useState([]);
  const [editedCombinedAmounts, setEditedCombinedAmounts] = useState([]);
  const [editedAmount, setEditedAmount] = useState(null);
  const [adSpend, setAdSpend] = useState(null);
  const [syncingFb, setSyncingFb] = useState(false);
  const [otherItems, setOtherItems] = useState([]);
  const [showAddOther, setShowAddOther] = useState(false);
  const [otherForm, setOtherForm] = useState({ service_name: "", hsn: "", description: "", amount: 0, separate_invoice: false });
  const SERVICE_CATALOG = [
    { name: "Design & Development", hsn: "998314" },
    { name: "Digital Marketing", hsn: "998319" },
    { name: "Domain Buy & Renewals", hsn: "998315" },
    { name: "Email Solutions", hsn: "998315" },
    { name: "Hosting Solutions", hsn: "998315" },
    { name: "Multimedia Production", hsn: "998319" },
    { name: "Website Development", hsn: "998314" },
    { name: "Website Maintenance", hsn: "998313" },
    { name: "Services", hsn: "998316" },
    { name: "SEO", hsn: "998313" },
  ];
  const previewRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [revRes, snapRes, combinedRes, monthlySnap, fbSpend, othersRes] = await Promise.all([
        axios.get(`${API}/monthly-revenue/${clientId}/${month}`),
        axios.get(`${API}/invoice-snapshot/${clientId}/${month}`),
        axios.get(`${API}/clients/${clientId}/combined-revenue/${month}`),
        axios.get(`${API}/clients/${clientId}/monthly-snapshot/${month}`),
        axios.get(`${API}/facebook/spend/${clientId}/${month}`).catch(() => ({ data: { data: null } })),
        axios.get(`${API}/other-items/${clientId}/${month}`).catch(() => ({ data: { items: [] } })),
      ]);
      setRevenueData(revRes.data.data);
      setAdjustments(revRes.data.adjustments || []);
      setSnapshot(snapRes.data.snapshot);
      setCombinedRevenue(combinedRes.data);
      setAdSpend(fbSpend.data.data);
      setOtherItems(othersRes.data.items || []);
      const snap = monthlySnap.data;
      setBillingCalc(snap?.commission_summary || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [clientId, month]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setInvoiceResults([]); setShowPreview(false); }, [month]);
  useEffect(() => { if (showPreview && previewRef.current) previewRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [showPreview]);

  const syncRevenue = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/revenue/${clientId}/${month}/sync`);
      fetchData();
      onRefresh?.();
    } catch (err) { alert("Sync failed: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncing(false); }
  };

  const syncFacebookSpend = async () => {
    setSyncingFb(true);
    try {
      const res = await axios.post(`${API}/facebook/sync-spend/${clientId}/${month}`);
      fetchData();
      onRefresh?.();
    } catch (err) { alert("Facebook sync failed: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncingFb(false); }
  };

  const addAdjustment = async () => {
    if (!adjForm.amount) return;
    await axios.post(`${API}/adjustments/${clientId}/${month}`, adjForm);
    setAdjForm({ adjustment_type: "plus", amount: 0, reference_no: "", note: "" });
    setShowAddAdjustment(false);
    fetchData();
    onRefresh?.();
  };

  const deleteAdjustment = async (id) => {
    await axios.delete(`${API}/adjustments/${id}`);
    fetchData();
    onRefresh?.();
  };

  const calculateSnapshot = async () => {
    try {
      const params = manualRevenue !== null ? `?revenue_override=${manualRevenue}` : "";
      await axios.post(`${API}/invoice-snapshot/${clientId}/${month}/calculate${params}`);
      setEditingRevenue(false);
      fetchData();
      onRefresh?.();
    } catch (err) { alert("Calculation failed: " + (err.response?.data?.detail || err.message)); }
  };

  const toggleLock = async () => {
    const action = snapshot?.is_locked ? "unlock" : "lock";
    await axios.post(`${API}/invoice-snapshot/${clientId}/${month}/${action}`);
    fetchData();
  };

  const fetchPreview = async (type, deduct = false, amt = null) => {
    setLoadingPreview(true);
    try {
      const p = new URLSearchParams({ invoice_type: type, deduct_fixed_fee: deduct });
      if (amt !== null) p.append("custom_deduction", amt);
      const res = await axios.post(`${API}/invoices/${clientId}/${month}/preview?${p}`);
      setInvoicePreview(res.data.preview); setPreviewType(type); setShowPreview(true);
      setEditedDescription(res.data.preview.description || "");
      setEditedAmount(null);
    } catch (err) { alert("Preview failed: " + (err.response?.data?.detail || err.message)); }
    finally { setLoadingPreview(false); }
  };

  const pushToZoho = async () => {
    setInvoicing(true);
    try {
      if (previewType === "combined") {
        const body = {
          descriptions: editedCombinedDescs.length > 0 ? editedCombinedDescs : undefined,
          amounts: editedCombinedAmounts.length > 0 ? editedCombinedAmounts : undefined,
        };
        const res = await axios.post(`${API}/invoices/${clientId}/${month}/create-combined`, body);
        if (res.data.success) {
          setInvoiceResults(prev => [...prev, { type: "combined", success: true, number: res.data.invoice?.zoho_invoice_number, amount: res.data.invoice?.amount }]);
          setShowPreview(false);
          onRefresh?.();
          fetchData();
        } else {
          setInvoiceResults(prev => [...prev, { type: "combined", success: false, error: res.data.error }]);
        }
      } else {
        const p = new URLSearchParams({ invoice_type: previewType, deduct_fixed_fee: deductFixed });
        if (customDeduction !== null) p.append("custom_deduction", customDeduction);
        if (editedDescription) p.append("custom_description", editedDescription);
        if (editedAmount !== null) p.append("custom_amount", editedAmount);
        const res = await axios.post(`${API}/invoices/${clientId}/${month}/create?${p}`);
        if (res.data.success) {
          setInvoiceResults(prev => [...prev, { type: previewType, success: true, number: res.data.invoice?.zoho_invoice_number, amount: res.data.invoice?.amount }]);
          setShowPreview(false);
          onRefresh?.();
          fetchData();
        } else {
          setInvoiceResults(prev => [...prev, { type: previewType, success: false, error: res.data.error }]);
        }
      }
    } catch (err) {
      setInvoiceResults(prev => [...prev, { type: previewType, success: false, error: err.response?.data?.detail || err.message }]);
    }
    finally { setInvoicing(false); }
  };

  const fetchCombinedPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/invoices/${clientId}/${month}/preview-combined`);
      setInvoicePreview(res.data.preview); setPreviewType("combined"); setShowPreview(true);
      setEditedCombinedDescs((res.data.preview.line_items || []).map(item => item.description || ""));
      setEditedCombinedAmounts((res.data.preview.line_items || []).map(item => item.amount ?? 0));
    } catch (err) { alert("Combined preview failed: " + (err.response?.data?.detail || err.message)); }
    finally { setLoadingPreview(false); }
  };

  if (loading) return <div className="p-4 text-[#71717A]">Loading billing data...</div>;

  const rev = revenueData || {};
  const isLocked = snapshot?.is_locked;
  const isCombined = combinedRevenue?.is_combined;
  const plusAdj = adjustments.filter(a => a.adjustment_type === "plus");
  const minusAdj = adjustments.filter(a => a.adjustment_type === "minus");
  const totalPlus = plusAdj.reduce((s, a) => s + a.amount, 0);
  const totalMinus = minusAdj.reduce((s, a) => s + a.amount, 0);
  const fixedFeeRef = snapshot?.fixed_fee || billingCalc?.fixed_fee || 0;
  const canCombineInvoice = clientData?.combine_invoices && (clientData?.billing_model === "both" || clientData?.billing_model === "whichever_higher");
  const displayRevenue = manualRevenue !== null ? manualRevenue : (snapshot?.effective_revenue || rev.adjusted_revenue || 0);

  return (
    <div className="space-y-4" data-testid="billing-config-panel">
      {/* === SECTION 1: Revenue === */}
      <div className="card-flat p-6" data-testid="revenue-breakdown">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Revenue - {monthLabel}</h3>
          <div className="flex items-center gap-2">
            <button onClick={syncRevenue} className="btn-secondary text-xs flex items-center gap-1" disabled={syncing} data-testid="sync-revenue-btn">
              <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Shopify"}
            </button>
            {!isLocked && (
              <button onClick={() => setShowAddAdjustment(!showAddAdjustment)} className="btn-secondary text-xs flex items-center gap-1" data-testid="add-adjustment-btn">
                <Plus size={14} /> Adjustment
              </button>
            )}
          </div>
        </div>

        {/* Combined Revenue Breakdown */}
        {isCombined && combinedRevenue.breakdown.length > 1 && (
          <div className="mb-4 p-3 bg-[#0A1A0A] border border-[#00E676]/20">
            <span className="small-label text-[#00E676]">Combined Revenue (Linked Clients)</span>
            {combinedRevenue.breakdown.map(cb => (
              <div key={cb.client_id} className="flex justify-between text-sm mt-1">
                <span>{cb.client_name}</span>
                <span className="data-number">{formatCurrency(cb.adjusted_revenue, currency)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm mt-2 pt-2 border-t border-[#00E676]/20 font-semibold">
              <span className="text-[#00E676]">Total Combined</span>
              <span className="data-number text-[#FFEB00]">{formatCurrency(combinedRevenue.total_combined_revenue, currency)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <span className="small-label">Total Sales</span>
            <p className="data-number mt-1 text-lg">{formatCurrency(rev.total_sales || rev.shopify_gross, currency)}</p>
            <p className="text-xs text-[#71717A] flex items-center gap-2">
              {rev.shopify_order_count || 0} orders
              <button
                onClick={resyncFromShopify}
                disabled={resyncing}
                className="text-[#FFEB00] hover:underline text-[10px] flex items-center gap-0.5"
                title="Fetch fresh from Shopify (bypasses 15-min cache)"
                data-testid="billing-resync-btn"
              >
                <ArrowsClockwise size={10} className={resyncing ? "animate-spin" : ""} weight="bold" />
                {resyncing ? "Syncing…" : "resync"}
              </button>
            </p>
          </div>
          <div>
            <span className="small-label">Returns</span>
            <p className="data-number mt-1 text-lg" style={{ color: (rev.returns || 0) !== 0 ? "#FF4444" : undefined }}>
              {(rev.returns || 0) !== 0 ? `-${formatCurrency(Math.abs(rev.returns || 0), currency)}` : formatCurrency(0, currency)}
            </p>
          </div>

          {/* Per-store breakdown */}
          {rev.store_breakdown?.length > 1 && (
            <div className="md:col-span-4">
              <span className="small-label mb-1 block">Per Store</span>
              <div className="flex flex-wrap gap-3">
                {rev.store_breakdown.map((sb, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded">
                    {sb.shop.replace(".myshopify.com", "")}:
                    {sb.fx_rate && sb.fx_rate !== 1 ? (
                      <> <span className="text-[#71717A]">{sb.original_currency} {sb.original_gross?.toLocaleString()}</span> <span className="text-[#FFEB00]">x{sb.fx_rate}</span> = {formatCurrency(sb.gross_sales + (sb.tax || 0) + (sb.shipping || 0), currency)}</>
                    ) : (
                      <> {formatCurrency(sb.gross_sales + (sb.tax || 0) + (sb.shipping || 0), currency)}</>
                    )}
                    <span className="text-[#71717A] ml-1">({sb.order_count} orders)</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
          <div>
            <span className="small-label">Manual Revenue</span>
            <p className="data-number mt-1 text-lg">{formatCurrency(rev.manual_revenue, currency)}</p>
          </div>
          <div>
            <span className="small-label">+ Adjustments ({plusAdj.length})</span>
            <p className="data-number mt-1 text-lg" style={{ color: totalPlus > 0 ? "#00E676" : undefined }}>
              {formatCurrency(totalPlus, currency)}
            </p>
          </div>
          <div>
            <span className="small-label">- Adjustments ({minusAdj.length})</span>
            <p className="data-number mt-1 text-lg" style={{ color: totalMinus > 0 ? "#FF4444" : undefined }}>
              {formatCurrency(totalMinus, currency)}
            </p>
          </div>
          <div>
            <span className="small-label">Adjusted Revenue</span>
            <p className="data-number mt-1 text-lg font-bold" style={{ color: "#FFEB00" }}>
              {formatCurrency(rev.adjusted_revenue, currency)}
            </p>
          </div>
        </div>

        {/* Manual Revenue Override */}
        {!isLocked && (
          <div className="mt-4 p-3 bg-[#1A1A00] border border-[#FFEB00]/20" data-testid="manual-revenue-section">
            <div className="flex items-center justify-between mb-2">
              <span className="small-label text-[#FFEB00]">Revenue for Billing</span>
              {!editingRevenue ? (
                <button onClick={() => { setEditingRevenue(true); setManualRevenue(displayRevenue); }} className="btn-secondary text-xs" data-testid="edit-revenue-btn">
                  Edit Revenue
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setManualRevenue(null); setEditingRevenue(false); }} className="btn-secondary text-xs" data-testid="reset-revenue-btn">
                    Reset
                  </button>
                </div>
              )}
            </div>
            {editingRevenue ? (
              <div className="flex items-center gap-3">
                <input type="number" className="input-default flex-1 text-lg font-mono" value={manualRevenue ?? ""}
                  onChange={e => setManualRevenue(parseFloat(e.target.value) || 0)} data-testid="manual-revenue-input" />
                <span className="text-sm text-[#71717A]">Click "Calculate" below to apply</span>
              </div>
            ) : (
              <p className="data-number text-xl font-bold" style={{ color: "#FFEB00" }}>
                {formatCurrency(displayRevenue, currency)}
                {snapshot?.revenue_override !== null && snapshot?.revenue_override !== undefined && (
                  <span className="text-xs text-[#FFEB00]/60 ml-2">(manually set)</span>
                )}
              </p>
            )}
          </div>
        )}

        {/* Ad Spend (Facebook) */}
        {(adSpend || syncingFb) && (
          <div className="mt-4 p-3 bg-[#0A0A1A] border border-[#8B5CF6]/20" data-testid="ad-spend-section">
            <div className="flex items-center justify-between mb-2">
              <span className="small-label text-[#8B5CF6]">Facebook Ad Spend</span>
              <button onClick={syncFacebookSpend} className="btn-secondary text-xs flex items-center gap-1" disabled={syncingFb} data-testid="sync-fb-spend-btn">
                <ArrowsClockwise size={14} className={syncingFb ? "animate-spin" : ""} /> {syncingFb ? "Syncing..." : "Sync"}
              </button>
            </div>
            {adSpend && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><span className="text-xs text-[#71717A]">Total Spend</span><p className="data-number text-lg" style={{color:'#8B5CF6'}}>{formatCurrency(adSpend.total_spend, currency)}</p></div>
                <div><span className="text-xs text-[#71717A]">Impressions</span><p className="data-number text-lg">{(adSpend.total_impressions || 0).toLocaleString()}</p></div>
                <div><span className="text-xs text-[#71717A]">Clicks</span><p className="data-number text-lg">{(adSpend.total_clicks || 0).toLocaleString()}</p></div>
                <div><span className="text-xs text-[#71717A]">ROAS</span><p className="data-number text-lg" style={{color: ((rev.adjusted_revenue || 0) / (adSpend.total_spend || 1)) >= 3 ? '#00C853' : '#FF4444'}}>
                  {adSpend.total_spend > 0 ? ((rev.adjusted_revenue || 0) / adSpend.total_spend).toFixed(2) + 'x' : '—'}
                </p></div>
              </div>
            )}
          </div>
        )}

        {/* Adjustment Entries List */}
        {adjustments.length > 0 && (
          <div className="mt-4" data-testid="adjustments-list">
            <table className="table-dense w-full">
              <thead><tr><th>Type</th><th>Ref No.</th><th className="text-right">Amount</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {adjustments.map(a => (
                  <tr key={a.id}>
                    <td>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${a.adjustment_type === "plus" ? "bg-[#00E676]/10 text-[#00E676]" : "bg-[#FF4444]/10 text-[#FF4444]"}`}>
                        {a.adjustment_type === "plus" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                        {a.adjustment_type === "plus" ? "Plus" : "Minus"}
                      </span>
                    </td>
                    <td className="text-sm font-mono">{a.reference_no || "—"}</td>
                    <td className="text-right data-number" style={{ color: a.adjustment_type === "plus" ? "#00E676" : "#FF4444" }}>
                      {a.adjustment_type === "minus" ? "-" : "+"}{formatCurrency(a.amount, currency)}
                    </td>
                    <td className="text-sm text-[#A1A1AA]">{a.note || "—"}</td>
                    <td className="text-right">
                      {!isLocked && (
                        <button onClick={() => deleteAdjustment(a.id)} className="text-[#FF4444] hover:text-[#FF6666] text-xs" data-testid={`delete-adj-${a.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Adjustment Form */}
        {showAddAdjustment && (
          <div className="mt-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A] space-y-3" data-testid="adjustment-form">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="form-label">Type</label>
                <select className="input-default w-full" value={adjForm.adjustment_type}
                  onChange={e => setAdjForm(p => ({ ...p, adjustment_type: e.target.value }))} data-testid="adj-type-select">
                  <option value="plus">Plus (+)</option><option value="minus">Minus (-)</option>
                </select>
              </div>
              <div>
                <label className="form-label">Amount</label>
                <input type="number" className="input-default w-full" value={adjForm.amount}
                  onChange={e => setAdjForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} data-testid="adj-amount-input" />
              </div>
              <div>
                <label className="form-label">Reference No.</label>
                <input type="text" className="input-default w-full" value={adjForm.reference_no} placeholder="ADJ-001"
                  onChange={e => setAdjForm(p => ({ ...p, reference_no: e.target.value }))} data-testid="adj-ref-input" />
              </div>
              <div>
                <label className="form-label">Note</label>
                <input type="text" className="input-default w-full" value={adjForm.note} placeholder="Reason"
                  onChange={e => setAdjForm(p => ({ ...p, note: e.target.value }))} data-testid="adj-note-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={addAdjustment} className="btn-primary text-xs" data-testid="save-adjustment-btn">Save Adjustment</button>
              <button onClick={() => setShowAddAdjustment(false)} className="btn-secondary text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* === SECTION 1B: Other Variable Items === */}
      <div className="card-flat p-6 border-l-4 border-l-[#3B82F6]" data-testid="other-items-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">Other Variable Items</h3>
            <p className="text-xs text-[#71717A]">Custom line items not tied to revenue (e.g. one-time charges, ad-hoc services)</p>
          </div>
          {!isLocked && (
            <button onClick={() => setShowAddOther(!showAddOther)} className="btn-secondary text-xs flex items-center gap-1" data-testid="add-other-item-btn">
              <Plus size={14} /> Add Item
            </button>
          )}
        </div>
        {otherItems.length > 0 && (
          <table className="table-dense w-full mb-3">
            <thead><tr><th>Service (HSN)</th><th>Project / Description</th><th className="text-right">Amount</th><th>Invoice</th><th></th></tr></thead>
            <tbody>
              {otherItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="text-sm">
                    {item.service_name ? (
                      <>
                        <span className="font-medium">{item.service_name}</span>
                        <span className="text-[#71717A] text-xs ml-2">({item.hsn || "—"})</span>
                      </>
                    ) : (
                      <span className="text-[#71717A] italic">Custom</span>
                    )}
                  </td>
                  <td className="text-sm text-[#A1A1AA]">{item.description || "—"}</td>
                  <td className="text-right data-number" style={{ color: "#3B82F6" }}>{formatCurrency(item.amount, currency)}</td>
                  <td className="text-xs">
                    {item.separate_invoice
                      ? <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider" style={{background:'#F59E0B22', color:'#F59E0B'}}>Separate</span>
                      : <span className="text-[#71717A]">Line item</span>}
                  </td>
                  <td className="text-right">
                    {!isLocked && (
                      <button onClick={async () => {
                        const updated = otherItems.filter((_, i) => i !== idx);
                        await axios.post(`${API}/other-items/${clientId}/${month}`, { items: updated });
                        setOtherItems(updated);
                      }} className="text-[#FF4444] hover:text-[#FF6666] text-xs" data-testid={`remove-other-item-${idx}`}><Trash size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-[#2A2A2A]">
                <td className="text-sm font-medium" colSpan={2}>Total Others</td>
                <td className="text-right data-number font-medium" style={{ color: "#3B82F6" }}>{formatCurrency(otherItems.reduce((s, i) => s + (i.amount || 0), 0), currency)}</td>
                <td className="text-xs text-[#71717A]" colSpan={2}>{otherItems.filter(i => i.separate_invoice).length} separate</td>
              </tr>
            </tbody>
          </table>
        )}
        {showAddOther && (
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="form-label">Service</label>
                <select
                  className="input-default w-full"
                  value={otherForm.service_name}
                  onChange={e => {
                    const chosen = SERVICE_CATALOG.find(s => s.name === e.target.value);
                    setOtherForm(p => ({ ...p, service_name: e.target.value, hsn: chosen?.hsn || "" }));
                  }}
                  data-testid="other-service-select"
                >
                  <option value="">Select service…</option>
                  {SERVICE_CATALOG.map(s => (
                    <option key={s.name} value={s.name}>{s.name} ({s.hsn})</option>
                  ))}
                </select>
                {otherForm.hsn && <p className="text-[10px] text-[#71717A] mt-1">HSN: {otherForm.hsn}</p>}
              </div>
              <div>
                <label className="form-label">Project / Description</label>
                <input type="text" className="input-default w-full" value={otherForm.description} placeholder="e.g. Logo redesign, Spring campaign"
                  onChange={e => setOtherForm(p => ({ ...p, description: e.target.value }))} data-testid="other-desc-input" />
              </div>
              <div>
                <label className="form-label">Amount</label>
                <input type="number" className="input-default w-full" value={otherForm.amount}
                  onChange={e => setOtherForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} data-testid="other-amount-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="other-separate-invoice-wrap">
              <input type="checkbox" className="w-4 h-4 accent-[#F59E0B]"
                checked={!!otherForm.separate_invoice}
                onChange={e => setOtherForm(p => ({ ...p, separate_invoice: e.target.checked }))}
                data-testid="other-separate-invoice-checkbox" />
              <span>Create a <span className="text-[#F59E0B] font-medium">separate Zoho invoice</span> for this item (uncheck to include as a line item on the main invoice)</span>
            </label>
            <div className="flex gap-2">
              <button onClick={async () => {
                if (!otherForm.service_name || !otherForm.amount) { alert("Please select a service and enter an amount."); return; }
                const updated = [...otherItems, {
                  service_name: otherForm.service_name,
                  hsn: otherForm.hsn,
                  description: otherForm.description,
                  amount: otherForm.amount,
                  separate_invoice: !!otherForm.separate_invoice,
                }];
                await axios.post(`${API}/other-items/${clientId}/${month}`, { items: updated });
                setOtherItems(updated);
                setOtherForm({ service_name: "", hsn: "", description: "", amount: 0, separate_invoice: false });
                setShowAddOther(false);
              }} className="btn-primary text-xs" data-testid="save-other-item-btn">Save Item</button>
              <button onClick={() => setShowAddOther(false)} className="btn-secondary text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* === SECTION 2: Billing Calculation === */}
      <div className="card-flat p-6 border-l-4 border-l-[#FFEB00]" data-testid="billing-calculation">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Billing Calculation</h3>
            {isLocked && <span className="text-xs px-2 py-1 bg-[#FFEB00] text-black rounded font-medium flex items-center gap-1"><Lock size={12} /> Locked</span>}
          </div>
          <div className="flex items-center gap-2">
            {snapshot && (
              <button onClick={toggleLock} className={`text-xs flex items-center gap-1 ${isLocked ? "btn-secondary" : "btn-primary"}`} data-testid="lock-snapshot-btn">
                {isLocked ? <><LockOpen size={14} /> Unlock</> : <><Lock size={14} /> Lock Month</>}
              </button>
            )}
            {!isLocked && (
              <button onClick={calculateSnapshot} className="btn-primary text-xs flex items-center gap-1" data-testid="calculate-snapshot-btn">
                <Check size={14} /> {snapshot ? "Recalculate" : "Calculate"}
              </button>
            )}
          </div>
        </div>

        {isLocked && (
          <div className="mb-3 px-3 py-2 bg-[#1A1A00] border border-[#FFEB00]/30 text-xs text-[#FFEB00]">
            This month is locked. Past calculations are preserved.
          </div>
        )}

        {/* Show either snapshot (calculated) or auto-calc from rules */}
        {(snapshot || billingCalc) ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <span className="small-label">Effective Revenue</span>
                <p className="data-number mt-1 text-lg">{formatCurrency(snapshot?.effective_revenue || billingCalc?.total_revenue, currency)}</p>
              </div>
              <div>
                <span className="small-label">Fixed Fee</span>
                <p className="data-number mt-1 text-lg">{formatCurrency(snapshot?.fixed_fee || billingCalc?.fixed_fee, currency)}</p>
              </div>
              <div>
                <span className="small-label">Variable Fee</span>
                <p className="data-number mt-1 text-lg">{formatCurrency(snapshot?.variable_fee || billingCalc?.variable_fee, currency)}</p>
              </div>
              <div>
                <span className="small-label">Total Billable</span>
                <p className="data-number mt-1 text-lg font-bold" style={{ color: "#FFEB00" }}>{formatCurrency(snapshot?.total_billable || billingCalc?.total_billable, currency)}</p>
              </div>
              <div>
                <span className="small-label">Commission</span>
                <p className="data-number mt-1 text-lg" style={{ color: "#00C853" }}>{formatCurrency(snapshot?.commission || billingCalc?.total_commission, currency)}</p>
              </div>
            </div>
            {!snapshot && billingCalc && (
              <p className="text-xs text-[#71717A] mt-3">Auto-calculated from Rules. Click "Calculate" to lock in these numbers for invoicing.</p>
            )}
          </>
        ) : (
          <p className="text-[#71717A] text-sm">No billing data yet. Sync revenue and click "Calculate".</p>
        )}
      </div>

      {/* === SECTION 3: Invoice Creation === */}
      <div className="card-flat p-6" data-testid="invoice-creation">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Invoice</h3>
        </div>

        {snapshot ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              {(snapshot.fixed_fee > 0 || billingCalc?.fixed_fee > 0) && (
                <button onClick={() => { setDeductFixed(false); setCustomDeduction(null); fetchPreview("fixed"); }}
                  className="btn-primary flex items-center gap-2" disabled={loadingPreview} data-testid="create-fixed-invoice-btn">
                  <Receipt size={18} /> Fixed Fee Invoice
                </button>
              )}
              {(snapshot.variable_fee > 0 || billingCalc?.variable_fee > 0) && (
                <button onClick={() => { setDeductFixed(false); setCustomDeduction(null); fetchPreview("variable"); }}
                  className="btn-primary flex items-center gap-2" disabled={loadingPreview} data-testid="create-variable-invoice-btn">
                  <Receipt size={18} /> Variable Fee Invoice
                </button>
              )}
              {canCombineInvoice && (
                <button onClick={fetchCombinedPreview}
                  className="btn-primary flex items-center gap-2 bg-[#8B5CF6] hover:bg-[#7C3AED]" disabled={loadingPreview} data-testid="create-combined-invoice-btn">
                  <Receipt size={18} /> Combined Invoice
                </button>
              )}
              {loadingPreview && <span className="text-[#FFEB00] text-sm">Loading...</span>}
            </div>

            {/* Invoice Results */}
            {invoiceResults.length > 0 && (
              <div className="space-y-2">{invoiceResults.map((r, i) => (
                <div key={i} className={`p-3 text-sm border ${r.success ? "bg-[#001A0D] border-[#00C853]/30 text-[#00C853]" : "bg-[#1A0000] border-[#FF2A2A]/30 text-[#FF2A2A]"}`}>
                  {r.success ? `${r.type} invoice created: #${r.number} (${formatCurrency(r.amount, currency)})` : `Failed: ${r.error}`}
                </div>
              ))}</div>
            )}

            {/* Invoice Preview */}
            {showPreview && invoicePreview && (
              <div ref={previewRef} className="border-2 border-[#FFEB00]/40 bg-[#0A0A0A]" data-testid="invoice-preview-panel">
                <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between bg-[#1A1A00]">
                  <h4 className="font-semibold text-[#FFEB00]">Invoice Preview - {previewType === "fixed" ? "Fixed Fee" : previewType === "combined" ? "Combined" : "Variable Fee"}</h4>
                  <button onClick={() => setShowPreview(false)} className="btn-ghost text-sm" data-testid="close-preview-btn"><X size={18} /></button>
                </div>
                <div className="p-6">
                  {previewType === "combined" ? (
                    /* Combined Invoice Preview */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="p-4 bg-[#111] border border-[#333] rounded mb-4">
                          <div className="flex justify-between mb-3 pb-2 border-b border-[#333]">
                            <div>
                              <p className="font-semibold">{invoicePreview.client_name}</p>
                              <p className="text-xs text-[#71717A]">Combined Invoice</p>
                            </div>
                            <div className="text-right">
                              <span className="small-label">Total Amount</span>
                              <p className="text-xl font-bold font-mono" style={{ color: "#FFEB00" }}>{formatCurrency(invoicePreview.total_amount, currency)}</p>
                            </div>
                          </div>
                        </div>
                        <table className="table-dense w-full">
                          <thead><tr><th>Line Item</th><th className="text-right">Rate</th><th className="text-right">Qty</th><th className="text-right">Amount</th></tr></thead>
                          <tbody>
                            {invoicePreview.line_items?.map((item, i) => {
                              const amt = editedCombinedAmounts[i] ?? item.amount;
                              return (
                                <tr key={i}>
                                  <td className="text-sm">{item.name}</td>
                                  <td className="text-right">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={amt}
                                      onChange={e => {
                                        const updated = [...editedCombinedAmounts];
                                        updated[i] = parseFloat(e.target.value) || 0;
                                        setEditedCombinedAmounts(updated);
                                      }}
                                      className="w-28 text-right bg-[#0A0A0A] border border-[#333] focus:border-[#FFEB00] focus:outline-none px-2 py-1 text-sm font-mono rounded"
                                      data-testid={`edit-combined-amount-${i}`}
                                    />
                                  </td>
                                  <td className="text-right">1</td>
                                  <td className="text-right data-number font-semibold">{formatCurrency(amt, currency)}</td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-[#FFEB00]/30">
                              <td colSpan={3} className="text-right font-semibold text-[#FFEB00]">Total</td>
                              <td className="text-right data-number font-bold text-[#FFEB00]">{formatCurrency(
                                editedCombinedAmounts.length > 0 ? editedCombinedAmounts.reduce((a, b) => a + (b || 0), 0) : invoicePreview.total_amount,
                                currency
                              )}</td>
                            </tr>
                          </tbody>
                        </table>
                        {invoicePreview.line_items?.map((item, i) => (
                          <div key={i} className="mt-3">
                            <span className="small-label mb-1 block">{item.name} — Description <span className="text-[#71717A] text-xs">(editable)</span></span>
                            <textarea className="w-full text-xs text-[#E4E4E7] font-mono bg-[#0A0A0A] p-3 rounded border border-[#333] focus:border-[#FFEB00] focus:outline-none resize-y min-h-[60px]"
                              value={editedCombinedDescs[i] ?? item.description} onChange={e => {
                                const updated = [...editedCombinedDescs];
                                updated[i] = e.target.value;
                                setEditedCombinedDescs(updated);
                              }} rows={5} data-testid={`edit-combined-desc-${i}`} />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-4">
                        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <span className="small-label">Fixed Fee ({invoicePreview.fixed_month_label})</span>
                          <p className="data-number text-2xl mt-1">{formatCurrency(invoicePreview.fixed_amount, currency)}</p>
                        </div>
                        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <span className="small-label">Variable Fee ({invoicePreview.variable_month_label})</span>
                          <p className="data-number text-2xl mt-1">{formatCurrency(invoicePreview.variable_amount, currency)}</p>
                          <p className="text-xs text-[#71717A] mt-1">Revenue: {formatCurrency(invoicePreview.variable_revenue, currency)}</p>
                        </div>
                        <div className="p-4 bg-[#1A1A00] border-2 border-[#FFEB00]/50">
                          <span className="small-label text-[#FFEB00]">Total Invoice Amount</span>
                          <p className="data-number text-3xl mt-1 font-bold" style={{ color: "#FFEB00" }} data-testid="net-invoice-amount">{formatCurrency(invoicePreview.total_amount, currency)}</p>
                        </div>
                        <button onClick={pushToZoho} disabled={invoicing} className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base" data-testid="push-to-zoho-btn">
                          <Receipt size={20} /> {invoicing ? "Pushing..." : "Push Invoice to Zoho Books"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Standard (Fixed/Variable) Invoice Preview */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="p-4 bg-[#111] border border-[#333] rounded mb-4">
                        <div className="flex justify-between mb-3 pb-2 border-b border-[#333]">
                          <div>
                            <p className="font-semibold">{invoicePreview.client_name}</p>
                            <p className="text-xs text-[#71717A]">{invoicePreview.month} | {invoicePreview.invoice_type} invoice</p>
                          </div>
                          <div className="text-right">
                            <span className="small-label">Invoice Amount</span>
                            <p className="text-xl font-bold font-mono" style={{ color: "#FFEB00" }}>{formatCurrency(invoicePreview.final_amount, currency)}</p>
                          </div>
                        </div>
                        <span className="small-label mb-1 block">Description <span className="text-[#71717A] text-xs">(editable)</span></span>
                        <textarea className="w-full text-xs text-[#E4E4E7] font-mono bg-[#0A0A0A] p-3 rounded border border-[#333] focus:border-[#FFEB00] focus:outline-none resize-y min-h-[80px]"
                          value={editedDescription} onChange={e => setEditedDescription(e.target.value)} rows={8} data-testid="edit-description-textarea" />
                      </div>
                      <table className="table-dense w-full">
                        <thead><tr><th>Item</th><th className="text-right">HSN</th><th className="text-right">Rate</th><th className="text-right">Qty</th><th className="text-right">Amount</th></tr></thead>
                        <tbody>
                          {invoicePreview.services?.length > 0 ? (
                            invoicePreview.services.map((svc, i) => (
                              <tr key={i}>
                                <td>{svc.name}</td>
                                <td className="text-right text-xs text-[#71717A]">{svc.hsn}</td>
                                <td className="text-right data-number">{formatCurrency(svc.fixed_cost, currency)}</td>
                                <td className="text-right">1</td>
                                <td className="text-right data-number font-semibold">{formatCurrency(svc.fixed_cost, currency)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td>{invoicePreview.client_name || "Digital Marketing"}</td>
                              <td className="text-right text-xs text-[#71717A]">998319</td>
                              <td className="text-right data-number">{formatCurrency(editedAmount !== null ? editedAmount : invoicePreview.final_amount, currency)}</td>
                              <td className="text-right">1</td>
                              <td className="text-right data-number font-semibold">{formatCurrency(editedAmount !== null ? editedAmount : invoicePreview.final_amount, currency)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {invoicePreview.deduction_amount > 0 && (
                        <p className="text-xs text-[#FFEB00] mt-2">* Fixed fee deduction of {formatCurrency(invoicePreview.deduction_amount, currency)} already applied in the amount above</p>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <span className="small-label">Gross Billable Amount</span>
                        <p className="data-number text-2xl mt-1" data-testid="gross-amount">{formatCurrency(invoicePreview.gross_amount, currency)}</p>
                      </div>
                      {previewType === "variable" && fixedFeeRef > 0 && (
                        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <div className="flex items-center gap-3 mb-3">
                            <input type="checkbox" id="deduct-fixed-billing" checked={deductFixed}
                              onChange={e => { setDeductFixed(e.target.checked); setCustomDeduction(null); fetchPreview(previewType, e.target.checked); }}
                              className="w-5 h-5 accent-[#FFEB00] cursor-pointer" data-testid="deduct-fixed-checkbox" />
                            <label htmlFor="deduct-fixed-billing" className="text-sm font-medium cursor-pointer">Reduce already billed fixed fee</label>
                          </div>
                          <div className="ml-8 space-y-3">
                            <div className="text-sm text-[#A1A1AA]">Fixed Fee: <span className="data-number">{formatCurrency(invoicePreview.fixed_fee_already_billed || fixedFeeRef, currency)}</span></div>
                            {deductFixed && (
                              <div className="form-group mb-0"><label className="form-label text-xs">Deduction Amount</label>
                                <input type="number" className="input-default w-full" value={customDeduction !== null ? customDeduction : invoicePreview.deduction_amount}
                                  onChange={e => { const v = parseFloat(e.target.value) || 0; setCustomDeduction(v); fetchPreview(previewType, true, v); }} data-testid="custom-deduction-input" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="p-4 bg-[#1A1A00] border-2 border-[#FFEB00]/50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="small-label text-[#FFEB00]">Net Invoice Amount</span>
                          <span className="text-xs text-[#71717A]">(editable)</span>
                        </div>
                        <input type="number" className="w-full bg-transparent text-3xl font-bold font-mono border-b border-[#FFEB00]/30 focus:border-[#FFEB00] outline-none py-1"
                          style={{ color: "#FFEB00" }}
                          value={editedAmount !== null ? editedAmount : invoicePreview.final_amount}
                          onChange={e => setEditedAmount(parseFloat(e.target.value) || 0)}
                          data-testid="net-invoice-amount" />
                        {deductFixed && invoicePreview.deduction_amount > 0 && <p className="text-xs text-[#A1A1AA] mt-1">({formatCurrency(invoicePreview.gross_amount, currency)} - {formatCurrency(invoicePreview.deduction_amount, currency)} deduction)</p>}
                      </div>
                      <button onClick={pushToZoho} disabled={invoicing} className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base" data-testid="push-to-zoho-btn">
                        <Receipt size={20} /> {invoicing ? "Pushing..." : "Push Invoice to Zoho Books"}
                      </button>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-[#71717A] text-sm">Calculate billing first to enable invoice creation.</p>
        )}
      </div>
    </div>
  );
};

export default BillingConfigPanel;
